/* Aanplant en verzorging page - Shows products from "Aanplant en verzorging" category only
 * Updated: Layout unified with other pages (breadcrumb, padding, bg-white, max-w-7xl).
 *          Product cards now exactly match the Assortiment section on the home page
 *          (300px height, 2.5rem radius, ShoppingCart quick-add button, whileInView animation).
 * Updated: Removed redundant filter sidebar and all filter-related state/functions.
 */

import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Loader as Loader2, Package, ShoppingCart } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface ProductVariant {
  id: string;
  product_id: string;
  size: string;
  type: string;
  price: number;
  product_name: string;
  product_slug: string;
  category_slug: string;
  scientific_name: string;
  images: string[];
  mainProductImage: string | null;
  btw_percentage: number;
  is_simple_product: boolean;
  minimum_order_quantity: number;
}

export default function AanplantEnVerzorgingPage() {
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const [addingToCart, setAddingToCart] = useState<string | null>(null);

  useEffect(() => {
    fetchVariants();
  }, []);

  const fetchVariants = async () => {
    try {
      setLoading(true);

      const { data: variantsData, error: variantsError } = await supabase
        .from('product_variants')
        .select(`
          id,
          product_id,
          size,
          type,
          price_tier_1,
          slug,
          minimum_order_quantity,
          products!inner(
            name,
            base_slug,
            scientific_name,
            btw_percentage,
            product_categories!inner(
              name,
              slug
            )
          )
        `)
        .eq('active', true)
        .order('size', { ascending: true });

      if (variantsError) throw variantsError;

      const { data: simpleProductsData, error: simpleProductsError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          base_slug,
          scientific_name,
          btw_percentage,
          simple_price,
          product_categories!inner(
            name,
            slug
          )
        `)
        .eq('is_simple_product', true)
        .eq('active', true);

      if (simpleProductsError) throw simpleProductsError;

      const formattedVariants: ProductVariant[] = variantsData.map((v: any) => {
        const btwPercentage = v.products.btw_percentage || 9;
        const tier1ExclBTW = parseFloat(v.price_tier_1 || '0');
        const tier1InclBTW = calculatePriceInclBTW(tier1ExclBTW, btwPercentage);

        return {
          id: v.id,
          product_id: v.product_id,
          size: v.size,
          type: v.type,
          price: tier1InclBTW,
          product_name: v.products.name,
          product_slug: v.slug || v.products.base_slug,
          category_slug: v.products.product_categories.slug,
          scientific_name: v.products.scientific_name,
          images: [],
          mainProductImage: null,
          btw_percentage: btwPercentage,
          is_simple_product: false,
          minimum_order_quantity: v.minimum_order_quantity || 1
        };
      });

      const formattedSimpleProducts: ProductVariant[] = simpleProductsData.map((p: any) => {
        const btwPercentage = p.btw_percentage || 21;
        const priceExclBTW = parseFloat(p.simple_price || '0');
        const priceInclBTW = calculatePriceInclBTW(priceExclBTW, btwPercentage);

        return {
          id: p.id,
          product_id: p.id,
          size: '-',
          type: '-',
          price: priceInclBTW,
          product_name: p.name,
          product_slug: p.base_slug,
          category_slug: p.product_categories.slug,
          scientific_name: p.scientific_name || '',
          images: [],
          mainProductImage: null,
          btw_percentage: btwPercentage,
          is_simple_product: true,
          minimum_order_quantity: 1
        };
      });

      const allProducts = [...formattedVariants, ...formattedSimpleProducts];
      const aanplantEnVerzorgingOnly = allProducts.filter(product =>
        product.category_slug === 'aanplant-en-verzorging'
      );

      const variantsWithImages = await Promise.all(
        aanplantEnVerzorgingOnly.map(async (variant) => {
          const { data: mainImageData } = await supabase
            .from('product_images')
            .select('storage_path, uses_new_bucket')
            .eq('product_id', variant.product_id)
            .is('variant_id', null)
            .eq('is_primary', true)
            .maybeSingle();

          let mainProductImage = null;
          if (mainImageData) {
            mainProductImage = getProductImageUrl(mainImageData.storage_path, mainImageData.uses_new_bucket);
          }

          let imageData = null;

          if (!variant.is_simple_product) {
            const result = await supabase
              .from('product_images')
              .select('storage_path, display_order, uses_new_bucket')
              .eq('variant_id', variant.id)
              .order('display_order', { ascending: true });
            imageData = result.data;

            if (!imageData || imageData.length === 0) {
              const { data: productImages } = await supabase
                .from('product_images')
                .select('storage_path, display_order, uses_new_bucket')
                .eq('product_id', variant.product_id)
                .is('variant_id', null)
                .order('display_order', { ascending: true });
              imageData = productImages;
            }
          } else {
            const { data: productImages } = await supabase
              .from('product_images')
              .select('storage_path, display_order, uses_new_bucket')
              .eq('product_id', variant.product_id)
              .is('variant_id', null)
              .order('display_order', { ascending: true });
            imageData = productImages;
          }

          const images = imageData?.map((img: any) => getProductImageUrl(img.storage_path, img.uses_new_bucket)) || [];

          return { ...variant, images, mainProductImage };
        })
      );

      setVariants(variantsWithImages);
    } catch (error) {
      console.error('Error fetching variants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddToCart = async (variant: ProductVariant, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAddingToCart(variant.id);
    try {
      if (variant.is_simple_product) {
        await addItem({
          product_id: variant.product_id,
          product_name: variant.product_name,
          variant_size: '',
          unit_price: variant.price,
          slug: variant.product_slug,
          image_url: variant.images[0] || variant.mainProductImage || '/assets/preview.webp',
          is_simple_product: true,
          btw_percentage: variant.btw_percentage,
        });
      } else {
        await addItem({
          product_variant_id: variant.id,
          product_name: variant.product_name,
          variant_size: variant.size,
          variant_type: variant.type as 'Kluit' | 'Pot' | 'Kale wortel',
          unit_price: variant.price,
          slug: variant.product_slug,
          image_url: variant.images[0] || variant.mainProductImage || '/assets/preview.webp',
          btw_percentage: variant.btw_percentage,
        });
      }
    } catch {
      toast.error('Kon product niet toevoegen aan winkelwagen');
    } finally {
      setAddingToCart(null);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Aanplant en verzorging - Buitn Planten</title>
          <meta name="description" content="Alles wat u nodig heeft voor de aanplant en verzorging van uw haagplanten. Bekijk ons assortiment aan grond, mest en tuinaccessoires." />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content="Aanplant en verzorging - Buitn Planten" />
          <meta property="og:description" content="Alles voor de aanplant en verzorging van uw haagplanten." />
          <meta property="og:url" content="https://buitnplanten.nl/aanplant-en-verzorging" />
          <meta property="og:type" content="website" />
          <link rel="canonical" href="https://buitnplanten.nl/aanplant-en-verzorging" />
        </Helmet>
        <ContactBar />
        <Header />
        <div className="flex min-h-screen items-center justify-center bg-white">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Aanplant en verzorging - Buitn Planten</title>
        <meta name="description" content="Alles wat u nodig heeft voor de aanplant en verzorging van uw haagplanten. Bekijk ons assortiment aan grond, mest en tuinaccessoires." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Aanplant en verzorging - Buitn Planten" />
        <meta property="og:description" content="Alles voor de aanplant en verzorging van uw haagplanten." />
        <meta property="og:url" content="https://buitnplanten.nl/aanplant-en-verzorging" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://buitnplanten.nl/aanplant-en-verzorging" />
      </Helmet>
      <ContactBar />
      <Header />

      <main className="pt-8 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Aanplant en verzorging' }]} />
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-jv-primary mb-4">
              Aanplant en verzorging
            </h1>
            <p className="text-lg md:text-xl text-black">
              Alles voor optimale aanplant en verzorging van je planten
            </p>
          </div>

          {variants.length === 0 ? (
            <EmptyState
              message="Er zijn momenteel geen producten in deze categorie."
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
              {variants.map((variant, index) => (
                <motion.a
                  key={variant.id}
                  href={`/${variant.product_slug}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group relative block overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                  style={{
                    borderTopLeftRadius: index % 2 === 0 ? '2.5rem' : 0,
                    borderBottomRightRadius: index % 2 === 0 ? '2.5rem' : 0,
                    borderTopRightRadius: index % 2 === 0 ? 0 : '2.5rem',
                    borderBottomLeftRadius: index % 2 === 0 ? 0 : '2.5rem',
                    height: '300px',
                  }}
                >
                  {/* Full background image */}
                  {variant.images.length > 0 ? (
                    <img
                      src={variant.images[0]}
                      alt={variant.product_name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-100">
                      <Package className="w-10 h-10 text-gray-300" />
                      <span className="text-gray-400 text-xs font-medium">Foto volgt snel!</span>
                    </div>
                  )}

                  {/* Bottom-to-top gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0) 65%)',
                    }}
                  />

                  {/* Price badge top-right */}
                  {variant.price > 0 && (
                    <div
                      className="absolute top-3 right-3 text-white text-xs font-bold px-2.5 py-1.5"
                      style={{
                        backgroundColor: '#1e2b22',
                        borderTopRightRadius: '10px',
                        borderBottomLeftRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 12px rgba(255,255,255,0.7)'
                      }}
                    >
                      {variant.is_simple_product ? '' : 'Vanaf '}€{variant.price.toFixed(2)}
                    </div>
                  )}

                  {/* Bottom content: name + cart button */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 py-4 gap-2">
                    <div className="flex-1 min-w-0">
                      {variant.scientific_name && (
                        <p className="text-xs text-white/70 italic mb-0.5 line-clamp-1">
                          {variant.scientific_name}
                        </p>
                      )}
                      <span
                        className="text-sm font-bold text-white leading-tight line-clamp-2 transition-all duration-200 group-hover:underline group-hover:decoration-[#5a6552] group-hover:decoration-2 [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]"
                      >
                        {variant.product_name}
                      </span>
                    </div>

                    {/* Quick add to cart */}
                    <motion.button
                      onClick={(e) => handleQuickAddToCart(variant, e)}
                      disabled={addingToCart === variant.id}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-[#5a6552] hover:bg-[#1e2b22] disabled:opacity-50 transition-colors shadow-lg"
                      style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}
                      aria-label={`${variant.product_name} toevoegen aan winkelwagen`}
                    >
                      <ShoppingCart className="w-4 h-4 text-white" />
                    </motion.button>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
